import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../../utils/api';
import { Calendar, Clock, MapPin, ChevronLeft } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const STATUS_LABELS = {
  pending: { label: 'بانتظار التأكيد', color: 'bg-amber-500/20 text-amber-400' },
  confirmed: { label: 'مؤكد', color: 'bg-green-500/20 text-green-400' },
  active: { label: 'جارٍ التنفيذ', color: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'مكتمل', color: 'bg-slate-500/20 text-slate-300' },
  cancelled: { label: 'ملغي', color: 'bg-red-500/20 text-red-400' },
};

export default function MyBookingsPage() {
  const [filter, setFilter] = useState('all');
  const [role, setRole] = useState('renter');

  const { data, isLoading } = useQuery(['my-bookings', role, filter], () =>
    api.get(`/bookings/my?role=${role}&status=${filter !== 'all' ? filter : ''}`).then(r => r.data)
  );

  const bookings = data?.bookings || [];

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold text-white">حجوزاتي</h1>
        <p className="text-slate-400 mt-1">إدارة ومتابعة جميع الحجوزات</p>
      </div>

      {/* Role Toggle */}
      <div className="flex gap-2">
        {[{ v: 'renter', l: 'حجوزاتي كمستأجر' }, { v: 'owner', l: 'حجوزات إعلاناتي' }].map(r => (
          <button key={r.v} onClick={() => setRole(r.v)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${role === r.v ? 'bg-primary-500 text-white' : 'bg-slate-700/50 text-slate-400 hover:text-white'}`}>
            {r.l}
          </button>
        ))}
      </div>

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        {[{ v: 'all', l: 'الكل' }, ...Object.entries(STATUS_LABELS).map(([v, d]) => ({ v, l: d.label }))].map(s => (
          <button key={s.v} onClick={() => setFilter(s.v)} className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${filter === s.v ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30' : 'bg-slate-700/50 text-slate-400 hover:text-white'}`}>
            {s.l}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>
      ) : bookings.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <Calendar size={48} className="text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-white mb-2">لا توجد حجوزات</h3>
          <p className="text-slate-400">لم يتم العثور على حجوزات بهذه الفلاتر</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map(b => (
            <Link key={b.id} to={`/dashboard/bookings/${b.uuid}`} className="block glass rounded-2xl p-5 hover:border-primary-500/30 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-white">{b.listing_title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_LABELS[b.status]?.color}`}>
                      {STATUS_LABELS[b.status]?.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <Calendar size={13} />
                      {format(new Date(b.start_time), 'dd MMM yyyy', { locale: ar })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={13} />
                      {format(new Date(b.start_time), 'HH:mm')} - {format(new Date(b.end_time), 'HH:mm')}
                    </span>
                    <span className="font-medium text-primary-400">{b.total_amount} {b.currency}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">#{b.booking_reference}</p>
                </div>
                <ChevronLeft size={18} className="text-slate-600 flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
